import { formatFullStreet } from "../../util/format";
import {
  mapCountry,
  mapDateDocumentToPageModel,
  mapDatePageModelToRequest,
  mapIsCrossBorderWorker,
  mapNationality,
  mapNationalityString,
  mapPhoneDocumentToPageModel,
  mapPhonePageModelToRequest,
  mapResidencePermit,
  mapUserToCorrespondenceLanguage,
} from "../shared/mappers";
import CardLimitMapper from "./cardholder-form/card-limit/mapper";
import {
  AnnualGrossIncome,
  CardholderModel,
  ResidencePermit,
} from "./cardholder-form/types";
import { defaultEmploymentInfoDocument, defaults } from "./defaults";
import { CardholderPageModel } from "./types";
import {
  LanguageBackendType,
  LanguageFrontendType,
} from "../shared/components/language/types";
import {
  CardApplicationDocument,
  CardApplicationDto,
  EmploymentInfo,
  NaturalPersonDocument,
} from "../../api/commercial/generated-types";
import { Gender } from "../../types";

const mapDocumentToPageModel = (
  document: CardApplicationDocument,
): CardholderPageModel => {
  const resultCardholder = document.cardholder;
  const resultEmploymentInfo = document.employmentInfo;

  const cardholderData: CardholderModel = {
    gender: resultCardholder.gender,
    firstName: resultCardholder.firstName,
    lastName: resultCardholder.lastName,
    fullStreet: formatFullStreet(resultCardholder.address),
    address: {
      ...resultCardholder.address,
      country: mapCountry(
        resultCardholder.address ? resultCardholder.address.country : null,
      ),
    },
    phoneBusiness: mapPhoneDocumentToPageModel(resultCardholder.phoneBusiness),
    mobile: mapPhoneDocumentToPageModel(resultCardholder.mobile),
    emailBusiness: resultCardholder.emailBusiness,
    birthdate: mapDateDocumentToPageModel(resultCardholder.birthdate),
    nationality: mapNationality(resultCardholder.nationality),
    correspondenceLanguage: resultCardholder.correspondenceLanguage,
    isCrossBorderWorker: resultEmploymentInfo.isCrossBorderWorker
      ? "yes"
      : "no",
    residencePermit:
      resultEmploymentInfo.residencePermit &&
      ResidencePermit[resultEmploymentInfo.residencePermit],
    companyPosition: resultEmploymentInfo.companyPosition,
    ...CardLimitMapper.mapDocumentToPageModel(document.limit),
    isCurrentPermanentEmployee: resultEmploymentInfo.isCurrentlyEmployed
      ? "yes"
      : "no",
    annualGrossIncome:
      resultEmploymentInfo.annualGrossIncome as AnnualGrossIncome,
  };

  return {
    cardholder: document.adminIsCardholder
      ? defaults.cardholder
      : cardholderData,
    adminAsCardholder: document.adminIsCardholder
      ? cardholderData
      : defaults.cardholder,
    isSameAsAdmin: document.adminIsCardholder ? "yes" : "no",
    hasDecentralizedInvoicing: document.hasDecentralizedInvoicing,
    decentralizedInvoicing: {
      bankAccount:
        document?.decentralizedInvoicing?.bankAccount ||
        defaults.decentralizedInvoicing.bankAccount,
      fundsOwnership: document?.decentralizedInvoicing?.fundsOwnership,
    },
    fundsOwnershipCardholder: {
      gender: document.fundsOwnershipCardholder?.gender,
      firstName: document.fundsOwnershipCardholder?.firstName,
      lastName: document.fundsOwnershipCardholder?.lastName,
      fullStreet: formatFullStreet(document.fundsOwnershipCardholder?.address),
      address: {
        ...document.fundsOwnershipCardholder?.address,
        country: mapCountry(
          document.fundsOwnershipCardholder?.address
            ? document.fundsOwnershipCardholder?.address?.country
            : null,
        ),
      },
      birthdate: mapDateDocumentToPageModel(
        document.fundsOwnershipCardholder?.birthdate,
      ),
      nationality: mapNationality(
        document.fundsOwnershipCardholder?.nationality,
      ),
    },
  };
};

const mapPageModelToRequest = (
  data: CardholderPageModel,
  selectedProduct: string,
): CardApplicationDto => {
  const cardholderData =
    data.isSameAsAdmin === "yes" ? data.adminAsCardholder : data.cardholder;
  return {
    productRef: selectedProduct,
    cardholder: {
      gender: cardholderData.gender as Gender,
      firstName: cardholderData.firstName,
      lastName: cardholderData.lastName,
      address: {
        ...cardholderData.address,
        country: mapCountry(cardholderData.address.country),
      },
      phoneBusiness: mapPhonePageModelToRequest(cardholderData.phoneBusiness),
      mobile: mapPhonePageModelToRequest(cardholderData.mobile),
      emailBusiness: cardholderData.emailBusiness,
      birthdate: mapDatePageModelToRequest(cardholderData.birthdate),
      nationality: mapNationalityString(cardholderData.nationality),
      correspondenceLanguage:
        cardholderData.correspondenceLanguage as LanguageBackendType,
      spendingLimit: CardLimitMapper.mapPageModelToRequest(
        cardholderData.spendingLimit,
        cardholderData.spendingLimitOther,
      ),
      isSameAsAdmin: data.isSameAsAdmin === "yes",
    },
    employmentInfo: {
      companyPosition: cardholderData.companyPosition,
      isCrossBorderWorker: mapIsCrossBorderWorker(
        cardholderData.isCrossBorderWorker,
        cardholderData.nationality,
        cardholderData.address.country,
      ),
      residencePermit: mapResidencePermit(
        cardholderData.residencePermit,
        cardholderData.isCrossBorderWorker,
        cardholderData.nationality,
        cardholderData.address.country,
      ),
      annualGrossIncome: cardholderData.annualGrossIncome,
      isCurrentlyEmployed: cardholderData.isCurrentPermanentEmployee === "yes",
      isInPermanentEmployment:
        cardholderData.isCurrentPermanentEmployee === "yes",
    },
    hasDecentralizedInvoicing: data.hasDecentralizedInvoicing,
    decentralizedInvoicing: {
      ...data.decentralizedInvoicing,
    },
    fundsOwnershipCardholder: {
      gender: data.fundsOwnershipCardholder.gender as Gender,
      firstName: data.fundsOwnershipCardholder.firstName,
      lastName: data.fundsOwnershipCardholder.lastName,
      address: {
        ...data.fundsOwnershipCardholder.address,
        country: mapCountry(data.fundsOwnershipCardholder.address.country),
      },
      birthdate: mapDatePageModelToRequest(
        data.fundsOwnershipCardholder.birthdate,
      ),
      nationality: mapNationalityString(
        data.fundsOwnershipCardholder.nationality,
      ),
    },
  };
};

const mapNaturalPersonDocumentToCardholderPageModel = (
  naturalPerson: NaturalPersonDocument,
  language: LanguageFrontendType,
  employmentInfo?: EmploymentInfo,
): CardholderModel => {
  const resultEmploymentInfo = employmentInfo
    ? employmentInfo
    : defaultEmploymentInfoDocument;

  return {
    gender: naturalPerson.gender,
    firstName: naturalPerson.firstName,
    lastName: naturalPerson.lastName,
    fullStreet: formatFullStreet(naturalPerson.address),
    address: {
      ...naturalPerson.address,
      country: mapCountry(
        naturalPerson.address ? naturalPerson.address.country : null,
      ),
    },
    phoneBusiness: mapPhoneDocumentToPageModel(naturalPerson.phoneBusiness),
    mobile: mapPhoneDocumentToPageModel(naturalPerson.mobile),
    emailBusiness: naturalPerson.emailBusiness,
    birthdate: mapDateDocumentToPageModel(naturalPerson.birthdate),
    nationality: mapNationality(naturalPerson.nationality),
    correspondenceLanguage:
      naturalPerson.correspondenceLanguage ||
      mapUserToCorrespondenceLanguage(language),
    isCrossBorderWorker: resultEmploymentInfo.isCrossBorderWorker
      ? "yes"
      : "no",
    residencePermit:
      resultEmploymentInfo.residencePermit &&
      ResidencePermit[resultEmploymentInfo.residencePermit],
    companyPosition: resultEmploymentInfo.companyPosition || "",
    // default is yes.
    isCurrentPermanentEmployee:
      resultEmploymentInfo.isCurrentlyEmployed === false ? "no" : "yes",
    annualGrossIncome:
      resultEmploymentInfo.annualGrossIncome as AnnualGrossIncome,
  };
};

const mapCardholderDocumentToPageModel = (
  document: CardApplicationDocument,
  language: LanguageFrontendType,
): CardholderModel => {
  const cardholder = mapNaturalPersonDocumentToCardholderPageModel(
    document.cardholder,
    language,
    document.employmentInfo,
  );
  return {
    ...cardholder,
    ...CardLimitMapper.mapDocumentToPageModel(document.limit),
  };
};

const CardholderBasicMapper = {
  mapPageModelToRequest,
  mapDocumentToPageModel,
  mapCardholderDocumentToPageModel,
  mapNaturalPersonDocumentToCardholderPageModel,
};

export default CardholderBasicMapper;
